import React, { useState } from 'react';

interface WelcomeModalProps {
    onClose: () => void;
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ onClose }) => {
    const [currentStep, setCurrentStep] = useState(0);

    const steps = [
        {
            title: '👋 Bienvenue sur Carnet de Mesures',
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700 text-lg">
                        L'application professionnelle pour gérer vos clients et leurs mesures.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                            <div className="text-2xl">📱</div>
                            <div>
                                <h4 className="font-semibold text-blue-900">Installez l'app</h4>
                                <p className="text-sm text-blue-700">Comme une vraie application mobile</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
                            <div className="text-2xl">✈️</div>
                            <div>
                                <h4 className="font-semibold text-green-900">Mode hors-ligne</h4>
                                <p className="text-sm text-green-700">Travaillez sans internet</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
                            <div className="text-2xl">☁️</div>
                            <div>
                                <h4 className="font-semibold text-purple-900">Synchronisation</h4>
                                <p className="text-sm text-purple-700">Vos données sécurisées</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg">
                            <div className="text-2xl">🎯</div>
                            <div>
                                <h4 className="font-semibold text-amber-900">Simple & Rapide</h4>
                                <p className="text-sm text-amber-700">Interface intuitive</p>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: '📱 Installation sur téléphone',
            content: (
                <div className="space-y-6">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                        <h4 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
                            <span className="text-2xl">🤖</span> Sur Android (Chrome)
                        </h4>
                        <ol className="space-y-3 text-sm text-gray-700">
                            <li className="flex gap-3">
                                <span className="font-bold text-blue-600">1.</span>
                                <span>Ouvrez le menu (⋮) en haut à droite</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="font-bold text-blue-600">2.</span>
                                <span>Sélectionnez <strong>"Installer l'application"</strong> ou <strong>"Ajouter à l'écran d'accueil"</strong></span>
                            </li>
                            <li className="flex gap-3">
                                <span className="font-bold text-blue-600">3.</span>
                                <span>Confirmez l'installation</span>
                            </li>
                        </ol>
                        <div className="mt-4 pt-4 border-t border-blue-100">
                            <p className="text-sm font-semibold text-blue-900 mb-2">🚀 Installation simplifiée :</p>
                            <a 
                                href="https://mesurette.com/app/carnet-mesures.apk"
                                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors shadow-sm"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Télécharger l'APK directement
                            </a>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-xl border border-gray-200">
                        <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <span className="text-2xl">🍎</span> Sur iPhone/iPad (Safari)
                        </h4>
                        <ol className="space-y-3 text-sm text-gray-700">
                            <li className="flex gap-3">
                                <span className="font-bold text-gray-600">1.</span>
                                <span>Appuyez sur le bouton <strong>Partager</strong> (□↑)</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="font-bold text-gray-600">2.</span>
                                <span>Faites défiler et choisissez <strong>"Sur l'écran d'accueil"</strong></span>
                            </li>
                            <li className="flex gap-3">
                                <span className="font-bold text-gray-600">3.</span>
                                <span>Appuyez sur <strong>"Ajouter"</strong></span>
                            </li>
                        </ol>
                    </div>

                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <p className="text-sm text-amber-800">
                            💡 <strong>Astuce :</strong> Une fois installée, l'app fonctionnera comme une vraie application mobile !
                        </p>
                    </div>
                </div>
            ),
        },
        {
            title: '✈️ Mode hors-ligne & Synchronisation',
            content: (
                <div className="space-y-6">
                    <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                        <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                            <span className="text-2xl">📴</span> Travaillez sans internet
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-start gap-2">
                                <span className="text-green-600">✓</span>
                                <span>Ajoutez et modifiez vos clients</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-600">✓</span>
                                <span>Prenez des mesures</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-600">✓</span>
                                <span>Consultez vos données à tout moment</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-600">✓</span>
                                <span>Toutes les modifications sont sauvegardées localement</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
                        <h4 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                            <span className="text-2xl">☁️</span> Synchronisation automatique
                        </h4>
                        <div className="space-y-3 text-sm text-gray-700">
                            <p>Dès que vous avez une connexion internet :</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-purple-600">→</span>
                                    <span>Cliquez sur votre nom en haut à droite</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-purple-600">→</span>
                                    <span>Appuyez sur <strong>"Synchroniser maintenant"</strong></span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-purple-600">→</span>
                                    <span>Vos données sont sauvegardées sur le serveur !</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800">
                            🔒 <strong>Sécurité :</strong> Vos données sont chiffrées et accessibles uniquement par vous.
                        </p>
                    </div>
                </div>
            ),
        },
        {
            title: '🚀 Commencez maintenant',
            content: (
                <div className="space-y-6">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mb-4">
                            <span className="text-4xl">🎉</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Vous êtes prêt !</h3>
                        <p className="text-gray-600">Voici comment commencer :</p>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-blue-300 transition">
                            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-xl font-bold text-blue-600">
                                1
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">Créez un compte</h4>
                                <p className="text-sm text-gray-600">Inscrivez-vous avec votre téléphone ou email</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-blue-300 transition">
                            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-xl font-bold text-blue-600">
                                2
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">Ajoutez vos premiers clients</h4>
                                <p className="text-sm text-gray-600">Cliquez sur "+ Nouveau Client" pour commencer</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-blue-300 transition">
                            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-xl font-bold text-blue-600">
                                3
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">Prenez des mesures</h4>
                                <p className="text-sm text-gray-600">Sélectionnez un client et cliquez sur "Ajouter Mesure"</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-xl border border-amber-200">
                        <p className="text-center text-sm text-gray-700">
                            💎 <strong>Formule gratuite :</strong> Jusqu'à 3 clients gratuitement<br />
                            <span className="text-xs text-gray-600">Passez Premium pour plus de clients et de fonctionnalités</span>
                        </p>
                    </div>
                </div>
            ),
        },
    ];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onClose();
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSkip = () => {
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-slideUp">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white relative">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold">{steps[currentStep].title}</h2>
                            <p className="text-blue-100 text-sm mt-1">
                                Étape {currentStep + 1} sur {steps.length}
                            </p>
                        </div>
                        <button
                            onClick={handleSkip}
                            className="text-white hover:text-gray-200 text-2xl font-bold transition"
                            aria-label="Fermer"
                        >
                            ×
                        </button>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-4 h-1 bg-blue-400 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-white transition-all duration-300"
                            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Content */}
                <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">{steps[currentStep].content}</div>

                {/* Footer */}
                <div className="bg-gray-50 px-8 py-6 flex justify-between items-center border-t border-gray-200">
                    <button
                        onClick={handlePrev}
                        disabled={currentStep === 0}
                        className="px-6 py-2 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        ← Précédent
                    </button>

                    <div className="flex gap-2">
                        {steps.map((_, index) => (
                            <div
                                key={index}
                                className={`w-2 h-2 rounded-full transition ${index === currentStep ? 'bg-blue-600 w-6' : 'bg-gray-300'
                                    }`}
                            />
                        ))}
                    </div>

                    <button
                        onClick={handleNext}
                        className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition shadow-lg"
                    >
                        {currentStep === steps.length - 1 ? 'Commencer' : 'Suivant →'}
                    </button>
                </div>
            </div>

            <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
        </div>
    );
};
